import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaYoutube, FaSpotify, FaApple, FaAmazon } from "react-icons/fa";
import { SiYoutubemusic } from "react-icons/si";
import site from "@/content/site.json";

// The five platform names, in the exact order requested by product.
// The href for each is looked up from site.json → watchAndListen.platforms
// (the same canonical source used by the homepage Watch & Listen section
// and the footer). This keeps a single source of truth for platform-level
// TATTVAM artist / channel / profile URLs — updating site.json updates
// every location.
const ORDER = ["YouTube", "Spotify", "Apple Music", "YouTube Music", "Amazon Music"];

const ICONS = {
  YouTube: FaYoutube,
  Spotify: FaSpotify,
  "Apple Music": FaApple,
  "YouTube Music": SiYoutubemusic,
  "Amazon Music": FaAmazon,
};

const TEST_IDS = {
  YouTube: "watch-listen-page-youtube",
  Spotify: "watch-listen-page-spotify",
  "Apple Music": "watch-listen-page-apple-music",
  "YouTube Music": "watch-listen-page-youtube-music",
  "Amazon Music": "watch-listen-page-amazon-music",
};

const findPlatform = (name) =>
  (site.watchAndListen && site.watchAndListen.platforms
    ? site.watchAndListen.platforms
    : []
  ).find((p) => p && p.name === name);

const WatchListenPage = () => {
 const trackClick = (destination, href) => {
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture("watch_listen_click", {
        destination,
        href,
        page: "/watch-listen",
      });
    }
  };
  // Client-side reinforcement of noindex, in case a crawler somehow
  // executes JS. The primary noindex tag is prerendered at build time
  // inside /build/watch-listen/index.html for non-JS crawlers.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "TATTVAM — Watch & Listen";

    const descTag =
      document.querySelector('meta[name="description"]') ||
      document.head.appendChild(
        Object.assign(document.createElement("meta"), { name: "description" }),
      );
    const prevDesc = descTag.getAttribute("content");
    descTag.setAttribute(
      "content",
      "Watch and listen to TATTVAM on YouTube, Spotify, Apple Music, YouTube Music and Amazon Music.",
    );

    let robotsTag = document.querySelector('meta[name="robots"]');
    const createdRobots = !robotsTag;
    if (!robotsTag) {
      robotsTag = document.createElement("meta");
      robotsTag.setAttribute("name", "robots");
      document.head.appendChild(robotsTag);
    }
    const prevRobots = robotsTag.getAttribute("content");
    robotsTag.setAttribute("content", "noindex, follow");

    return () => {
      document.title = prevTitle;
      if (prevDesc != null) descTag.setAttribute("content", prevDesc);
      if (createdRobots) {
        robotsTag.remove();
      } else if (prevRobots != null) {
        robotsTag.setAttribute("content", prevRobots);
      }
    };
  }, []);

  return (
    <main
      data-testid="watch-listen-page"
      className="min-h-screen bg-ivory text-charcoal flex items-start md:items-center justify-center px-6 py-16 md:py-20"
    >
      <div className="w-full max-w-md mx-auto flex flex-col items-center text-center">
        <img
          src={site.brand.logoFull}
          alt={site.brand.logoFullAlt || "TATTVAM"}
          className="h-16 md:h-20 w-auto"
          data-testid="watch-listen-logo"
        />

        <p
          className="mt-10 text-xs tracking-[0.32em] uppercase font-semibold text-muted_gold"
          data-testid="watch-listen-eyebrow"
        >
          Scholar-Verified Sanskrit Stotrams
        </p>

        <h1
          className="mt-4 font-heading font-normal text-charcoal text-2xl md:text-3xl leading-snug"
          data-testid="watch-listen-heading"
        >
          Watch, listen &amp; explore TATTVAM
        </h1>

        <div className="mt-6 w-10 h-px bg-muted_gold" />

        <ul
          className="mt-10 w-full flex flex-col gap-3"
          data-testid="watch-listen-buttons"
        >
          {ORDER.map((name) => {
            const p = findPlatform(name);
            if (!p || !p.href) return null;
            const Icon = ICONS[name];
            return (
              <li key={name}>
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick(name, p.href)}
                  data-testid={TEST_IDS[name]}
                  className="group flex items-center justify-center gap-3 w-full border border-charcoal/25 bg-ivory text-charcoal px-6 py-4 text-xs tracking-[0.28em] uppercase font-medium hover:border-muted_gold hover:text-muted_gold transition-colors duration-500"
                >
                  {Icon ? (
                    <Icon
                      className="text-base opacity-80 group-hover:opacity-100"
                      aria-hidden="true"
                    />
                  ) : null}
                  <span>{name}</span>
                </a>
              </li>
            );
          })}
        </ul>

        <div className="mt-5 flex flex-col items-center">
          <div className="w-6 h-px bg-charcoal/20" />
          <Link
            to="/"
            onClick={() => trackClick("Explore TATTVAM", "/")}
            data-testid="watch-listen-explore-link"
            className="mt-3 text-xs tracking-[0.24em] uppercase font-semibold text-charcoal/80 underline underline-offset-4 decoration-charcoal/35 hover:text-muted_gold hover:decoration-muted_gold transition-colors duration-500"
          >
            Explore TATTVAM ›
          </Link>
        </div>
      </div>
    </main>
  );
};

export default WatchListenPage;
